/**
 * Decorative art for the three guided-demo chapters (#2824), in the look of
 * the public How it works page: sprite characters, the desk calendar page
 * and the lead's monitor with a plain mock screen. Every piece is
 * `aria-hidden` and ignores the pointer.
 */
import "./demo-chapter-art.css";

const SPRITES = "/site/characters";

function Sprite({ src, className }: { src: string; className: string }) {
  return <img src={`${SPRITES}/${src}.svg`} alt="" draggable={false} className={className} />;
}

/** Chapter 1: three people standing together, greeting. */
function TeamGreeting() {
  return (
    <div className="dc-art dc-team" aria-hidden="true" data-testid="demo-chapter-art">
      <span className="dc-floor" />
      <Sprite src="sam/celebrate" className="dc-person dc-p1" />
      <Sprite src="maya/point" className="dc-person dc-p2" />
      <Sprite src="jo/celebrate" className="dc-person dc-p3" />
    </div>
  );
}

/** Chapter 2: the desk calendar page, with one person beside it. */
function SolveCalendar() {
  return (
    <div className="dc-art dc-solve" aria-hidden="true" data-testid="demo-chapter-art">
      <span className="dc-floor" />
      <div className="dc-cal">
        <span className="dc-cal-rings">
          {["r1", "r2", "r3", "r4", "r5", "r6"].map((k) => (
            <i key={k} />
          ))}
        </span>
        <small>Oct</small>
        <b>20</b>
        <small>Tue</small>
      </div>
      <Sprite src="alex/point" className="dc-person dc-p4" />
    </div>
  );
}

const MOCK_ROWS = ["a", "b", "c", "d"] as const;
const MOCK_CELLS = ["1", "2", "3", "4", "5", "6"] as const;

/** Chapter 3: the monitor with a plain page mock. */
function PageMonitor() {
  return (
    <div className="dc-art dc-page" aria-hidden="true" data-testid="demo-chapter-art">
      <div className="dc-monitor">
        <div className="dc-bezel">
          <div className="dc-screen">
            <div className="dc-bar">
              <i />
              <i />
              <i />
            </div>
            <div className="dc-mock">
              <span className="dc-side" />
              <div className="dc-grid">
                {MOCK_ROWS.map((row, r) => (
                  <div key={row} className="dc-row">
                    {MOCK_CELLS.map((cell, c) => (
                      <span key={cell} data-on={(r + c) % 3 === 0 ? "" : undefined} />
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <span className="dc-neck" />
        <span className="dc-foot" />
      </div>
    </div>
  );
}

/** The art for chapter `index` (0 to 2). */
export function DemoChapterArt({ index }: { index: number }) {
  if (index === 0) return <TeamGreeting />;
  if (index === 1) return <SolveCalendar />;
  return <PageMonitor />;
}

/** A prop from `/site/characters/props`, placed by its CSS class. */
function Prop({ src, className }: { src: string; className: string }) {
  return (
    <img
      src={`${SPRITES}/props/${src}.svg`}
      alt=""
      draggable={false}
      className={`dc-prop ${className}`}
    />
  );
}

/** Night shift ward: a door with a cross, a clock, two people. */
function HospitalsScene() {
  return (
    <div className="dc-art dc-ind dc-hospitals" aria-hidden="true" data-testid="demo-industry-art">
      <span className="dc-floor" />
      <Prop src="hospital-door" className="dc-i1" />
      <Prop src="wall-clock" className="dc-i2" />
      <Sprite src="maya/point" className="dc-person dc-s1" />
      <Sprite src="sam/idle" className="dc-person dc-s2" />
    </div>
  );
}

/** Gate booth with a lit window under a small moon. */
function SecurityScene() {
  return (
    <div className="dc-art dc-ind dc-security" aria-hidden="true" data-testid="demo-industry-art">
      <span className="dc-floor" />
      <Prop src="moon" className="dc-i2" />
      <Prop src="guard-booth" className="dc-i1" />
      <Sprite src="jo/idle" className="dc-person dc-s1" />
      <Sprite src="alex/point" className="dc-person dc-s2" />
    </div>
  );
}

/** Crew with a cabin bag and a plane tail fin. */
function AirlineScene() {
  return (
    <div className="dc-art dc-ind dc-airline" aria-hidden="true" data-testid="demo-industry-art">
      <span className="dc-floor" />
      <Prop src="plane-tail" className="dc-i1" />
      <Sprite src="sam/walk-a" className="dc-person dc-s1" />
      <Sprite src="maya/idle" className="dc-person dc-s2" />
      <Prop src="cabin-bag" className="dc-i2" />
    </div>
  );
}

/** Plant floor: a conveyor with boxes and a hard hat. */
function PlantsScene() {
  return (
    <div className="dc-art dc-ind dc-plants" aria-hidden="true" data-testid="demo-industry-art">
      <span className="dc-floor" />
      <Prop src="conveyor" className="dc-i1" />
      <Prop src="hard-hat" className="dc-i2" />
      <Sprite src="alex/idle" className="dc-person dc-s1" />
      <Sprite src="jo/point" className="dc-person dc-s2" />
    </div>
  );
}

/** Kitchen: a stove with steam and a rail of order tickets. */
function KitchensScene() {
  return (
    <div className="dc-art dc-ind dc-kitchens" aria-hidden="true" data-testid="demo-industry-art">
      <span className="dc-floor" />
      <Prop src="order-tickets" className="dc-i2" />
      <Prop src="stove" className="dc-i1" />
      <Sprite src="sam/point" className="dc-person dc-s1" />
      <Sprite src="maya/idle" className="dc-person dc-s2" />
    </div>
  );
}

const INDUSTRY_SCENES: Record<string, () => React.JSX.Element> = {
  hospitals: HospitalsScene,
  security: SecurityScene,
  airline: AirlineScene,
  plants: PlantsScene,
  kitchens: KitchensScene,
};

/** The scene for an industry card; an industry with no scene falls back to
 * the chapter art at `fallback`. */
export function DemoIndustryArt({
  industryId,
  fallback,
}: { industryId: string; fallback: number }) {
  const Scene = INDUSTRY_SCENES[industryId];
  if (!Scene) return <DemoChapterArt index={fallback} />;
  return <Scene />;
}
